import type { Permission } from '@glory/contracts';
import { type QueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { useMemo } from 'react';

import { meQuery } from './session.ts';

/**
 * Права смотрящего на фронте: единственный способ спросить «можно ли» — по разрешению из каталога
 * `@glory/identity/domain`, а не по роли. Опечатка в названии разрешения — ошибка компиляции.
 */
export interface PermissionCheck {
  readonly can: (permission: Permission) => boolean;
  readonly canAny: (...permissions: readonly Permission[]) => boolean;
}

export function permissionCheck(granted: readonly Permission[]): PermissionCheck {
  const set = new Set(granted);
  return {
    can: (permission) => set.has(permission),
    canAny: (...permissions) => permissions.some((permission) => set.has(permission)),
  };
}

/** Права текущего пользователя: экраны внутри приложения открываются после входа, профиль уже в кэше. */
export function usePermissions(): PermissionCheck {
  const { data: me } = useSuspenseQuery(meQuery);
  return useMemo(() => permissionCheck(me.permissions), [me.permissions]);
}

interface GuardContext {
  readonly context: { readonly queryClient: QueryClient };
}

/** Куда уводит охрана маршрута: раздел приложения — на «Сегодня», подраздел настроек — на первый общий. */
type GuardFallback = '/today' | '/settings/notifications';

/**
 * Охрана маршрута (`beforeLoad`): без хотя бы одного из разрешений — переход на «Сегодня», а не пустой экран или ошибка
 * API. Профиль берётся из кэша, без лишнего запроса.
 */
export function requirePermissionRoute(
  ...permissions: readonly [Permission, ...Permission[]]
): (guard: GuardContext) => Promise<void> {
  return guardRoute('/today', permissions);
}

/** То же для подраздела настроек: без права — на «Уведомления», первый подраздел, открытый всем. */
export function requireSettingsPermission(
  ...permissions: readonly [Permission, ...Permission[]]
): (guard: GuardContext) => Promise<void> {
  return guardRoute('/settings/notifications', permissions);
}

function guardRoute(
  fallback: GuardFallback,
  permissions: readonly Permission[],
): (guard: GuardContext) => Promise<void> {
  return async ({ context }) => {
    const me = await context.queryClient.query({ ...meQuery, staleTime: 'static' });
    if (!permissionCheck(me.permissions).canAny(...permissions))
      throw redirect({ to: fallback, replace: true });
  };
}
